import { Component, OnInit, Inject, ElementRef, ViewChild } from '@angular/core';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppUsersService, AppsService, AuthService, UserAppsService } from 'app/services';
import { Roles, UserApp } from 'app/models';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { GenericRoles } from 'app/helpers/constants';
import { environment } from 'environments/environment';
interface Data {
  clientId: any;
  app: UserApp;
  isInternal?: boolean;
  title: string;
  message: string;
  email: FormControl;
}
@Component({
  standalone: false,
  selector: 'app-add-user-dialog',
  templateUrl: './add-user-dialog.component.html',
  styleUrls: ['./add-user-dialog.component.scss']
})
export class AddUserDialogComponent implements OnInit {
  userCtrl = new FormControl('', [Validators.minLength(3), Validators.required,  
    (control) => {
    const value = control.value ? control.value.toString().trim() : '';
    if (!value || value.length < environment.minimumSearchLength) {
      return { minlength: true };
    }
    return null;
  }]);

  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;
  public roles: Roles[];
  public availableRoles: Roles[];
  private onDestroy$ = new Subject<void>();
  private searchValSubject = new BehaviorSubject<string>('');
  public items: any[];
  public selectedUser: any;
  public appUsers: any[];
  public appUsersCount: number;
  searchVal$ = this.searchValSubject.asObservable();
  destroy = new Subject<boolean>();
  error: string;
  filteredUsers: Observable<any[]>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: Data,
    public dialogRef: MatDialogRef<AddUserDialogComponent>,
    public _appUsersService: AppUsersService,
    public _appService: AppsService,
    private userAppsService: UserAppsService,
    private authService : AuthService
  ) {}

  ngOnInit() {
    this._appUsersService.clearSearchUsers();
    this.userCtrl.valueChanges
      .pipe(takeUntil(this.onDestroy$), debounceTime(600))
      .subscribe(res => {
        if (res !== '' && res != null) {
          const encodedName =  encodeURIComponent(this.userCtrl.value);
          this._appUsersService.searchAppUsers(
            encodedName.trim(),
            this.data.clientId,
            false
          );
        } else {
          this.appUsers = [];
        }
      });
    this.subscribeToRoles();
    this._appService.getRoles(this.data.clientId);
    this.getAppUsers();
    this.filteredUsers = this.userCtrl.valueChanges.pipe(
      startWith(null),
      map((searchValue: string | null) =>
      (typeof searchValue === 'string' ? this._filter(searchValue, this.appUsers) : this.appUsers.slice())),
    );

  }

  private _filter(value: string, users: any[]): any[] {
    const filterValue = value.toLowerCase();
    return users.filter((user) => {
      const userProperties = Object.values(user);
      return userProperties.some((property) => {
        if (typeof property === 'string') {
          return property.toLowerCase().includes(filterValue);
        } else if (typeof property === 'number') {
          return property.toString().includes(filterValue);
        }
        return false;
      });
    });
  }

  subscribeToRoles(): void {
    this._appService.appRoles$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(roles => {

        if(this.authService.user.isExternal){
          roles = roles.filter(e => !e.hideRoleForExternalUser);
        }
        
        this.availableRoles = roles;
        const userRole = roles.filter(e =>
          e.name.toLowerCase().includes(GenericRoles.userRole.toLowerCase())
        );
        if (userRole.length > 0) {
          userRole[0].hasAccess = true;
        }
      });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.selectedUser = event.option.value;
    this.userCtrl.setValue(this.selectedUser.name)
    if (!this.selectedUser.isInternal) {
      this.roles = this.availableRoles.filter(
        e => e.name.toLowerCase() !== GenericRoles.appManagerRole.toLowerCase()
      );
    } else {
      this.roles = this.availableRoles;
    }
    this.sortRoles();
  }


  sortRoles(){
    const userRole = this.roles.filter( role => role.name.toLowerCase() === GenericRoles.userRole.toLowerCase());
    if(userRole.length > 0)
    {
      this.roles = this.roles.filter( role =>role.name.toLowerCase() !== GenericRoles.userRole.toLowerCase())
      this.roles.unshift(userRole[0])
    }

  }

  getAppUsers(): void {
    this._appUsersService.searchUser$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (data: any) => {
          if (data && data.length > 0) {
            data.forEach(e => {
              e['isInternal'] = e.email.endsWith('allegion.com');
            });
            this.appUsers = data;
            this.filteredUsers = this.userCtrl.valueChanges.pipe(
              startWith(null),
              map((searchValue: string | null) =>
              (typeof searchValue === 'string' ? this._filter(searchValue, this.appUsers) : this.appUsers.slice())),
            );
          } else {
            this.appUsers = [];
          }
        },
        error => (this.error = error)
      );
  }

  resetUser() {
    if (this.selectedUser?.name.length > 0){
      this.selectedUser = null;}
  }

  saveUser() {
    this.data.app.roles = this.roles;
    this.userAppsService.saveApps(
      this.selectedUser.id,
      [this.data.app],
      false,
      this.dialogRef,
      null,
      this.data.clientId
    );
  }

  cancel() {
    this.dialogRef.close(false);
  }

  disableSave(){
    let selectedRolesCount = 0;
    this.roles?.map((role)=> (role.hasAccess)? selectedRolesCount++ : selectedRolesCount)
    return selectedRolesCount > 0;
  }
}
